import type { PackageFolder, RangePackage } from "./types"

type ImpactPackage = Pick<RangePackage, "id" | "name" | "folderId" | "storesFolderId" | "storeIds">
type ImpactFolder = Pick<PackageFolder, "id" | "name" | "parentId" | "ownStores">

export type FolderImpactRow = {
  id: string
  name: string
  /** "Follows folder", "Own set replaced", or "<subfolder>'s set replaced". */
  source: string
  /** True when Apply replaces a set of its own or its subfolder's. */
  replaced: boolean
  before: number
  gained: string[]
  lost: string[]
}

export type FolderStoreImpact = {
  rows: FolderImpactRow[]
  /** Stores every package has after Apply. */
  after: number
  /** Packages whose stores differ after Apply. */
  changing: number
  /** Packages with a set of their own (or their subfolder's) that Apply replaces. */
  replaced: number
}

/** Which folder's stores a package that follows this folder reads today. */
function storesOwnerId(folder: ImpactFolder) {
  return folder.parentId === null || folder.ownStores ? folder.id : folder.parentId
}

/**
 * What "Apply to all" does to each package in the folder and its subfolders: every one ends up
 * with exactly `staged`, including those with a set of their own.
 */
export function folderStoreImpact({
  folder,
  folders,
  packages,
  staged,
}: {
  folder: ImpactFolder
  folders: ImpactFolder[]
  /** The packages in the folder and its subfolders. */
  packages: ImpactPackage[]
  staged: ReadonlySet<string>
}): FolderStoreImpact {
  const owner = storesOwnerId(folder)
  const names = new Map(folders.map((other) => [other.id, other.name]))
  const rows = packages.map((pkg) => {
    const before = new Set(pkg.storeIds)
    const gained = [...staged].filter((storeId) => !before.has(storeId))
    const lost = pkg.storeIds.filter((storeId) => !staged.has(storeId))
    const replaced = pkg.storesFolderId !== owner
    return {
      id: pkg.id,
      name: pkg.name,
      source: sourceLabel(pkg.storesFolderId, owner, names),
      replaced,
      before: before.size,
      gained,
      lost,
    }
  })
  return {
    rows,
    after: staged.size,
    changing: rows.filter((row) => row.gained.length + row.lost.length > 0).length,
    replaced: rows.filter((row) => row.replaced).length,
  }
}

function sourceLabel(storesFolderId: string | null, owner: string, names: Map<string, string>) {
  if (storesFolderId === owner) return "Follows folder"
  if (storesFolderId === null) return "Own set replaced"
  return `${names.get(storesFolderId) ?? "Subfolder"}'s set replaced`
}

/** How many of the packages carry each store today. */
export function storeCarryCounts(packages: Pick<RangePackage, "storeIds">[]) {
  const counts = new Map<string, number>()
  for (const pkg of packages) {
    for (const storeId of pkg.storeIds) counts.set(storeId, (counts.get(storeId) ?? 0) + 1)
  }
  return counts
}
